import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { factory } from "../api/apiFactory";
import useDebounce from "./hooks/useDebounce";

const productApi = factory.get("product");

// Free-text chip list for serial numbers. Barcode scanners type the code then
// send Enter; Autocomplete's freeSolo Enter handler preventDefaults it, so the
// scan becomes a chip instead of submitting the surrounding form.
//
// Pass productId to suggest that product's existing serials (GET
// /api/v1/product-serials), and userId to narrow them to the ones that user
// holds. Without productId the field is plain free text, which is what
// registering brand new serials on a purchase invoice needs. Suggestions
// are fetched here rather than through Redux so that sibling instances on the
// same form do not overwrite each other's options.
const SerialNumbersInput = ({
  value,
  onChange,
  quantity,
  productId,
  userId,
  ...props
}) => {
  const serials = value ?? [];
  const matches = serials.length === quantity;

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const search = useDebounce(inputValue.trim(), 500);

  useEffect(() => {
    if (!productId) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    productApi
      .serials({
        product_id: productId,
        serial_number: search || undefined,
        user_id: userId || undefined,
      })
      .then((res) => {
        if (cancelled) return;
        // This endpoint returns a bare array, unlike the wrapped {data:{data}}
        // most of the API uses.
        const rows = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        setOptions(rows.map((row) => row.serial_number).filter(Boolean));
      })
      .catch(() => !cancelled && setOptions([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [productId, userId, search]);

  return (
    <Autocomplete
      multiple
      freeSolo
      filterSelectedOptions
      filterOptions={(x) => x}
      options={options}
      loading={loading}
      value={serials}
      inputValue={inputValue}
      onInputChange={(e, newInput) => setInputValue(newInput)}
      onChange={(e, newValue) => {
        // Scanners often append whitespace and people rescan the same box.
        const cleaned = newValue
          .map((serial) => String(serial).trim())
          .filter(Boolean);
        onChange([...new Set(cleaned)]);
        setInputValue("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="الارقام التسلسلية"
          helperText={
            matches
              ? `${serials.length}/${quantity}`
              : `عدد الارقام التسلسلية ${serials.length} والعدد ${quantity}`
          }
          error={!matches}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...props}
    />
  );
};

export default SerialNumbersInput;
